import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  MessageSquareQuote,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
  Video,
  X,
} from "lucide-react";
import {
  deleteTestimonial,
  fetchTestimonials,
} from "../../../Redux/custmorTestimonials/slice";
import { QueryDto, Testimonial } from "../../../Redux/custmorTestimonials/types";
import { AppDispatch } from "../../../store";
import { useAppSelector } from "../../../Redux/hooks";
import DateRangePicker from "../../../ecommerce/globalcomponents/DateRangePicker";
import "./UserTestimonialsPage.css";

interface FilterState {
  page: number;
  limit: number;
  status: string;
}

const cleanUrl = (url?: string | null) =>
  url?.replace(/^https?:\/\/localhost:\d+/, "") || "";

const FALLBACK_IMAGE =
  "https://res.cloudinary.com/dgomh6r4g/image/upload/v1708899806/placeholder_l7w6l4.png";

const EMPTY_TESTIMONIALS: Testimonial[] = [];

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const RatingStars = ({ rating }: { rating: number }) => (
  <div
    className="testimonials-rating"
    aria-label={`${rating} out of 5 stars`}
  >
    {Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={15}
        strokeWidth={2}
        className={index < rating ? "is-filled" : ""}
        fill={index < rating ? "currentColor" : "none"}
      />
    ))}
  </div>
);

const UserTestimonials = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { testimonials, isLoading } = useAppSelector(
    (state) => state.testimonials,
  );
  const { user } = useAppSelector((state) => state.customerAuth);

  const [filters, setFilters] = useState<FilterState>({
    page: 1,
    limit: 10,
    status: "",
  });
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    dispatch(fetchTestimonials(filters as QueryDto));
  }, [dispatch, filters]);

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field !== "page" ? { page: 1 } : {}),
    }));
  };

  const handleDelete = (id: string) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this testimonial?",
      );
      if (confirmed) {
        try {
          dispatch(deleteTestimonial(id))
            .unwrap()
            .then((res: any) => {
              if (res.status === "success") {
                dispatch(fetchTestimonials(filters as QueryDto));
              }
            });
        } catch (error: any) {
          console.log(error);
        }
      }
    } catch (error: any) {
      alert(error.message);
    }
  };

  const allTestimonials: Testimonial[] =
    testimonials?.data ?? EMPTY_TESTIMONIALS;

  const visibleTestimonials = useMemo(() => {
    const query = search.trim().toLowerCase();

    return allTestimonials.filter((t) => {
      const matchesSearch =
        !query ||
        t.title?.toLowerCase().includes(query) ||
        t.content?.toLowerCase().includes(query);

      const createdDay = t.createdAt ? t.createdAt.slice(0, 10) : "";
      const matchesFrom = !dateRange.from || createdDay >= dateRange.from;
      const matchesTo = !dateRange.to || createdDay <= dateRange.to;

      return matchesSearch && matchesFrom && matchesTo;
    });
  }, [allTestimonials, search, dateRange]);

  const totalCount = testimonials?.total || allTestimonials.length;
  const totalPages = testimonials?.totalPages || 0;
  const canGoPrev = filters.page > 1;
  const canGoNext =
    allTestimonials.length >= filters.limit &&
    (totalPages === 0 || filters.page < totalPages);

  const authorName =
    user?.fullName || user?.name || user?.registerId || "You";

  return (
    <section className="testimonials-page">
      <div className="testimonials-shell">
        <header className="testimonials-header">
          <div className="testimonials-header-main">
            <div className="testimonials-header-icon">
              <MessageSquareQuote size={22} strokeWidth={2} />
            </div>
            <div>
              <span className="testimonials-eyebrow">USER TESTIMONIALS</span>
              <h1>Testimonials</h1>
              <p>
                View your shared experiences, ratings and media in one place.
              </p>
            </div>
          </div>
          <div className="testimonials-count-pill">
            {totalCount} testimonials
          </div>
        </header>

        <div className="testimonials-toolbar">
          <div className="testimonials-toolbar-left">
            <label className="testimonials-page-size">
              <select
                value={filters.limit}
                onChange={(e) =>
                  handleFilterChange("limit", e.target.value)
                }
                aria-label="Entries per page"
              >
                {[10, 25, 50, 75, 100].map((val) => (
                  <option value={val} key={val}>
                    {val} Entries
                  </option>
                ))}
              </select>
            </label>

            <label className="testimonials-page-size">
              <select
                value={filters.status}
                onChange={(e) =>
                  handleFilterChange("status", e.target.value)
                }
                aria-label="Status filter"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />
          </div>

          <div className="testimonials-toolbar-right">
            <label className="testimonials-search">
              <Search size={17} strokeWidth={2} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search testimonials..."
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </label>

            <Link to="/CreateTestimonials" className="testimonials-create-btn">
              <Plus size={17} strokeWidth={2.2} />
              Create Testimonial
            </Link>
          </div>
        </div>

        <div className="testimonials-result-bar">
          <span>
            Showing <strong>{visibleTestimonials.length}</strong> of{" "}
            <strong>{allTestimonials.length}</strong> testimonials
          </span>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : visibleTestimonials.length > 0 ? (
          <div className="testimonials-grid">
            {visibleTestimonials.map((testimonial) => (
              <article
                key={testimonial._id}
                className={`testimonial-card ${
                  testimonial.isDeleted ? "is-deleted" : ""
                }`}
                style={
                  testimonial.isDeleted
                    ? { opacity: 0.65 }
                    : undefined
                }
              >
                <div className="testimonial-media">
                  <Link to={`/UpdateTestimonial/${testimonial._id}`}>
                    <img
                      src={cleanUrl(testimonial?.image) || FALLBACK_IMAGE}
                      alt={testimonial.title}
                    />
                  </Link>
                  <div className="testimonial-media-badges">
                    <span>
                      <ImageIcon size={13} />
                      Photo
                    </span>
                    {testimonial.video && (
                      <span>
                        <Video size={13} />
                        Video
                      </span>
                    )}
                  </div>
                </div>

                <div className="testimonial-card-body">
                  <div className="testimonial-title-row">
                    <h2>{testimonial.title}</h2>
                    <RatingStars rating={testimonial.rating || 0} />
                  </div>

                  <p>{testimonial.content}</p>

                  <footer className="testimonial-card-footer">
                    <span>
                      {authorName}
                      {" · "}
                      <span
                        className={`badge ${
                          testimonial.status === "active"
                            ? "bg-success"
                            : "bg-danger"
                        }`}
                      >
                        {testimonial.status}
                      </span>
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <time dateTime={testimonial.createdAt}>
                        {formatDate(testimonial.createdAt)}
                      </time>
                      {!testimonial.isDeleted && (
                        <Link
                          to={`/UpdateTestimonial/${testimonial._id}`}
                          aria-label="Edit testimonial"
                          style={{ color: "#173c72" }}
                        >
                          <Pencil size={15} />
                        </Link>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(testimonial._id)}
                        disabled={testimonial.isDeleted}
                        aria-label="Delete testimonial"
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#c53030",
                          padding: 0,
                          cursor: testimonial.isDeleted
                            ? "not-allowed"
                            : "pointer",
                        }}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </footer>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="testimonials-empty">
            <MessageSquareQuote size={28} strokeWidth={1.8} />
            <h2>No testimonials found</h2>
            <p>Try changing the search text, status or selected date range.</p>
          </div>
        )}

        <nav
          className="testimonials-pagination"
          aria-label="Testimonials pagination"
        >
          <button
            type="button"
            onClick={() =>
              handleFilterChange("page", String(filters.page - 1))
            }
            disabled={!canGoPrev}
            aria-label="Previous page"
          >
            <ChevronLeft size={17} />
          </button>
          <span>
            Page <strong>{filters.page}</strong>
            {totalPages > 0 && (
              <>
                {" of "}
                <strong>{totalPages}</strong>
              </>
            )}
          </span>
          <button
            type="button"
            onClick={() =>
              handleFilterChange("page", String(filters.page + 1))
            }
            disabled={!canGoNext}
            aria-label="Next page"
          >
            <ChevronRight size={17} />
          </button>
        </nav>
      </div>
    </section>
  );
};

export default UserTestimonials;
